import * as THREE from "three";
import { isSolid, WATER } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import { isItem, stackLimit, type ItemStack } from "../world/items";
import { mulberry32 } from "../core/rng";

/**
 * Dropped item entities.
 *
 * Blocks and containers hand their contents to this manager instead of writing
 * straight into the player's pack. It is deliberately renderer-free: the game
 * feeds it a small block-view so the physics and pickup rules can be tested
 * without WebGL or a generated world.
 */

/** Seconds an item survives, measured only while its chunk is loaded and active. */
export const ITEM_DESPAWN_SECONDS = 300;
/** Seconds after spawning before an item can be attracted or collected. */
export const ITEM_PICKUP_DELAY = 0.5;
/** Centre-to-centre distance at which an item starts flying to the player. */
export const ITEM_MAGNET_RADIUS = 2.25;
/** Centre-to-centre distance at which an item enters the pack. */
export const ITEM_PICKUP_RADIUS = 1.1;
/** Compatible stacks closer than this merge into one entity. */
export const ITEM_MERGE_RADIUS = 0.6;
/** Safety valve so a pathological spill cannot grow the scene without bound. */
export const MAX_DROPPED_ITEMS = 256;
/** Half-width and half-height of an item's collision box. */
export const ITEM_HALF = 0.125;

/** Vertical offset from the player's feet used as the pickup target. */
const PLAYER_PICKUP_HEIGHT = 0.9;
const ITEM_GRAVITY = 24;
const ITEM_TERMINAL_VELOCITY = 40;
const ITEM_MAGNET_SPEED = 7;
const ITEM_MAX_STEP = 0.1;
const ITEM_MAX_SUBSTEP = 0.25;
const WATER_BUOYANCY = 30;
const WATER_RISE_SPEED = 1.6;
const MERGE_INTERVAL = 0.5;
const EPS = 1e-4;

/** The slice of World needed by dropped items. */
export interface BlockView {
  /** Read without generating terrain. */
  peekBlock(wx: number, wy: number, wz: number): number;
  /** Optional so tests can use a tiny flat world. */
  isChunkLoaded?(wx: number, wz: number): boolean;
}

export interface DroppedItem {
  readonly id: number;
  /** Mutable because nearby compatible entities merge their counts. */
  stack: ItemStack;
  readonly position: THREE.Vector3;
  readonly velocity: THREE.Vector3;
  /** Seconds this entity has existed while active and loaded. */
  age: number;
  /** Seconds until the magnet and pickup can touch this entity. */
  pickupDelay: number;
}

export class DroppedItems {
  readonly items: DroppedItem[] = [];
  private nextId = 1;
  private readonly rand: () => number;
  private mergeTimer = 0;

  constructor(
    private readonly world: BlockView,
    seed = 1,
  ) {
    this.rand = mulberry32(seed);
  }

  /**
   * Spawn one or more entities for a stack. Over-limit stacks are split so the
   * pack's normal 64-stack rules always apply when they are collected.
   */
  spawn(
    stack: ItemStack,
    x: number,
    y: number,
    z: number,
    velocity?: { x: number; y: number; z: number },
  ): DroppedItem[] {
    const id = Math.floor(stack.id);
    const count = Math.floor(stack.count);
    if (!isItem(id) || !Number.isFinite(stack.count) || count <= 0) return [];

    const limit = Math.max(1, stackLimit(id));
    const created: DroppedItem[] = [];
    let left = count;

    while (left > 0) {
      const pieceCount = Math.min(limit, left);
      left -= pieceCount;

      while (this.items.length >= MAX_DROPPED_ITEMS) this.items.shift();
      const item = this.create(
        { ...stack, id, count: pieceCount },
        x,
        y,
        z,
        velocity,
      );
      this.items.push(item);
      created.push(item);
    }

    return created;
  }

  /**
   * Advance physics, merging, pickup and despawning. Returns the stacks that
   * entered the player's pack this tick.
   *
   * Items in unloaded chunks are frozen, so walking away cannot make them fall
   * through terrain that is no longer in memory.
   */
  update(dt: number, playerPosition: THREE.Vector3): ItemStack[] {
    if (!Number.isFinite(dt) || dt <= 0 || this.items.length === 0) return [];

    const collected: ItemStack[] = [];
    const step = Math.min(dt, ITEM_MAX_STEP);

    this.mergeTimer -= dt;
    if (this.mergeTimer <= 0) {
      this.mergeTimer = MERGE_INTERVAL;
      this.mergeNearby();
    }

    for (let i = this.items.length - 1; i >= 0; i--) {
      const item = this.items[i];
      if (!this.isLoaded(item.position.x, item.position.z)) continue;

      item.age += dt;
      if (item.age >= ITEM_DESPAWN_SECONDS) {
        this.items.splice(i, 1);
        continue;
      }

      if (item.pickupDelay > 0) {
        item.pickupDelay = Math.max(0, item.pickupDelay - dt);
        if (item.pickupDelay > 0) {
          this.applyPhysics(item, step);
          this.move(item, step);
          continue;
        }
      }

      const targetY = playerPosition.y + PLAYER_PICKUP_HEIGHT;
      const dx = playerPosition.x - item.position.x;
      const dy = targetY - item.position.y;
      const dz = playerPosition.z - item.position.z;
      const distance2 = dx * dx + dy * dy + dz * dz;

      if (distance2 <= ITEM_PICKUP_RADIUS * ITEM_PICKUP_RADIUS) {
        collected.push(item.stack);
        this.items.splice(i, 1);
        continue;
      }

      if (distance2 <= ITEM_MAGNET_RADIUS * ITEM_MAGNET_RADIUS) {
        const distance = Math.sqrt(distance2) || 1;
        const blend = Math.min(1, dt * 12);
        const vx = (dx / distance) * ITEM_MAGNET_SPEED;
        const vy = (dy / distance) * ITEM_MAGNET_SPEED;
        const vz = (dz / distance) * ITEM_MAGNET_SPEED;
        item.velocity.x += (vx - item.velocity.x) * blend;
        item.velocity.y += (vy - item.velocity.y) * blend;
        item.velocity.z += (vz - item.velocity.z) * blend;
        this.move(item, step);
      } else {
        this.applyPhysics(item, step);
        this.move(item, step);
      }

      if (item.position.y < -8) this.items.splice(i, 1);
    }

    return collected;
  }

  clear(): void {
    this.items.length = 0;
    this.nextId = 1;
    this.mergeTimer = 0;
  }

  private create(
    stack: ItemStack,
    x: number,
    y: number,
    z: number,
    velocity?: { x: number; y: number; z: number },
  ): DroppedItem {
    const angle = this.rand() * Math.PI * 2;
    const speed = 0.7 + this.rand() * 1.3;
    return {
      id: this.nextId++,
      stack,
      position: new THREE.Vector3(x, Math.max(ITEM_HALF, y), z),
      velocity: new THREE.Vector3(
        velocity?.x ?? Math.cos(angle) * speed,
        velocity?.y ?? 2.1 + this.rand() * 1.1,
        velocity?.z ?? Math.sin(angle) * speed,
      ),
      age: 0,
      pickupDelay: ITEM_PICKUP_DELAY,
    };
  }

  /** Merge same-id stacks that have settled close together. */
  private mergeNearby(): void {
    for (let i = 0; i < this.items.length; i++) {
      const a = this.items[i];
      if (!this.canMerge(a)) continue;

      for (let j = i + 1; j < this.items.length; j++) {
        const b = this.items[j];
        if (!this.canMerge(b)) continue;
        if (a.stack.id !== b.stack.id) continue;
        if (a.stack.durability !== b.stack.durability) continue;

        const limit = Math.max(1, stackLimit(a.stack.id));
        const room = limit - a.stack.count;
        if (room <= 0) break;
        if (a.position.distanceToSquared(b.position) > ITEM_MERGE_RADIUS * ITEM_MERGE_RADIUS) {
          continue;
        }

        const moved = Math.min(room, b.stack.count);
        a.stack = { ...a.stack, count: a.stack.count + moved };
        b.stack = { ...b.stack, count: b.stack.count - moved };
        if (b.stack.count <= 0) this.items.splice(j--, 1);
        if (a.stack.count >= limit) break;
      }
    }
  }

  private canMerge(item: DroppedItem): boolean {
    return item.pickupDelay <= 0 && this.isLoaded(item.position.x, item.position.z);
  }

  private applyPhysics(item: DroppedItem, dt: number): void {
    const block = this.world.peekBlock(
      Math.floor(item.position.x),
      Math.floor(item.position.y),
      Math.floor(item.position.z),
    );

    if (block === WATER) {
      item.velocity.y = Math.min(WATER_RISE_SPEED, item.velocity.y + WATER_BUOYANCY * dt);
      const drag = Math.max(0, 1 - dt * 2.5);
      item.velocity.x *= drag;
      item.velocity.z *= drag;
      return;
    }

    item.velocity.y -= ITEM_GRAVITY * dt;
    if (item.velocity.y < -ITEM_TERMINAL_VELOCITY) item.velocity.y = -ITEM_TERMINAL_VELOCITY;
    const drag = Math.max(0, 1 - dt * 0.4);
    item.velocity.x *= drag;
    item.velocity.z *= drag;
  }

  /** Move one axis at a time and snap flush against solid voxels. */
  private move(item: DroppedItem, dt: number): void {
    this.moveAxis(item, 0, item.velocity.x * dt);
    this.moveAxis(item, 1, item.velocity.y * dt);
    this.moveAxis(item, 2, item.velocity.z * dt);

    if (this.grounded(item)) {
      const friction = Math.max(0, 1 - dt * 10);
      item.velocity.x *= friction;
      item.velocity.z *= friction;
      if (Math.abs(item.velocity.x) < 0.02) item.velocity.x = 0;
      if (Math.abs(item.velocity.z) < 0.02) item.velocity.z = 0;
    }
  }

  private moveAxis(item: DroppedItem, axis: 0 | 1 | 2, delta: number): void {
    if (delta === 0) return;
    const steps = Math.max(1, Math.ceil(Math.abs(delta) / ITEM_MAX_SUBSTEP));
    const inc = delta / steps;
    const key = axis === 0 ? "x" : axis === 1 ? "y" : "z";

    for (let s = 0; s < steps; s++) {
      item.position[key] += inc;
      if (!this.collides(item.position)) continue;

      if (inc > 0) {
        item.position[key] = Math.floor(item.position[key] + ITEM_HALF) - ITEM_HALF - EPS;
      } else {
        item.position[key] = Math.floor(item.position[key] - ITEM_HALF) + 1 + ITEM_HALF + EPS;
      }

      if (axis === 1) {
        item.velocity.y = inc < 0 && item.velocity.y < -3 ? -item.velocity.y * 0.16 : 0;
      } else if (axis === 0) {
        item.velocity.x = 0;
      } else {
        item.velocity.z = 0;
      }
    }
  }

  /** Does the item's AABB overlap a solid voxel? */
  private collides(pos: THREE.Vector3): boolean {
    const minX = Math.floor(pos.x - ITEM_HALF + EPS);
    const maxX = Math.floor(pos.x + ITEM_HALF - EPS);
    const minY = Math.floor(pos.y - ITEM_HALF + EPS);
    const maxY = Math.floor(pos.y + ITEM_HALF - EPS);
    const minZ = Math.floor(pos.z - ITEM_HALF + EPS);
    const maxZ = Math.floor(pos.z + ITEM_HALF - EPS);

    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        for (let x = minX; x <= maxX; x++) {
          if (this.blockSolid(x, y, z)) return true;
        }
      }
    }
    return false;
  }

  private blockSolid(wx: number, wy: number, wz: number): boolean {
    if (wy < 0) return true;
    if (wy >= WORLD_HEIGHT) return false;
    if (this.world.isChunkLoaded && !this.world.isChunkLoaded(wx, wz)) return true;
    return isSolid(this.world.peekBlock(wx, wy, wz));
  }

  private grounded(item: DroppedItem): boolean {
    return this.blockSolid(
      Math.floor(item.position.x),
      Math.floor(item.position.y - ITEM_HALF - 0.02),
      Math.floor(item.position.z),
    );
  }

  private isLoaded(x: number, z: number): boolean {
    if (!this.world.isChunkLoaded) return true;
    return this.world.isChunkLoaded(Math.floor(x), Math.floor(z));
  }
}
